import type { IRoleRepository } from '@src/domain/rbac/repository/role_repository';
import type { Role } from '@src/domain/rbac/entities/role.entity';
import type { CreateRoleData } from '@src/domain/rbac/value_objects/create_role_data';
import type { UpdateRoleData } from '@src/domain/rbac/value_objects/update_role_data';
import type { ApiClient } from '../api/client';
import { RoleMapper } from '@src/application/rbac/mappers/role.mapper';
import type {
    RoleDTO,
    CreateRoleDTO,
    UpdateRoleDTO,
    AssignPermissionsDTO,
    RemovePermissionsDTO,
    AssignPermissionDTO,
    AssignRoleToUserDTO,
    AssignRolesToUserDTO,
    RemoveRolesFromUserDTO,
    PermissionOperationResponseDTO,
    AssignPermissionResponseDTO,
} from '@src/application/rbac/dto';

/**
 * Role Repository Implementation
 * Implements the IRoleRepository interface using HTTP API calls
 */
export class RoleRepositoryImpl implements IRoleRepository {
    private readonly apiClient: ApiClient;

    constructor(apiClient: ApiClient) {
        this.apiClient = apiClient;
    }

    async listAll(): Promise<Role[]> {
        const data = await this.apiClient.get<RoleDTO[]>('/rbac/roles');
        return RoleMapper.toDomainList(data);
    }

    async getById(roleId: string): Promise<Role | null> {
        try {
            const data = await this.apiClient.get<RoleDTO>(`/rbac/roles/${roleId}`);
            return RoleMapper.toDomain(data);
        } catch (error) {
            // If 404, return null
            if ((error as any).status === 404) {
                return null;
            }
            throw error;
        }
    }

    async create(data: CreateRoleData): Promise<Role> {
        const requestData: CreateRoleDTO = {
            name: data.name,
            description: data.description,
        };

        const response = await this.apiClient.post<RoleDTO>('/rbac/roles', requestData);
        return RoleMapper.toDomain(response);
    }

    async update(roleId: string, data: UpdateRoleData): Promise<Role> {
        const requestData: UpdateRoleDTO = {};

        if (data.name !== undefined) {
            requestData.name = data.name;
        }
        if (data.description !== undefined) {
            requestData.description = data.description;
        }
        if (data.isActive !== undefined) {
            requestData.is_active = data.isActive;
        }

        const response = await this.apiClient.put<RoleDTO>(
            `/rbac/roles/${roleId}`,
            requestData
        );
        return RoleMapper.toDomain(response);
    }

    async delete(roleId: string): Promise<void> {
        await this.apiClient.delete(`/rbac/roles/${roleId}`);
    }

    async assignPermissions(
        roleId: string,
        permissionNames: string[]
    ): Promise<{ message: string; assignedPermissions: string[] }> {
        const requestData: AssignPermissionsDTO = {
            permission_names: permissionNames,
        };

        const response = await this.apiClient.post<PermissionOperationResponseDTO>(
            `/rbac/roles/${roleId}/permissions/batch`,
            requestData
        );

        return {
            message: response.message,
            assignedPermissions: response.assigned_permissions || [],
        };
    }

    async removePermissions(
        roleId: string,
        permissionNames: string[]
    ): Promise<{ message: string; removedPermissions: string[] }> {
        const requestData: RemovePermissionsDTO = {
            permission_names: permissionNames,
        };

        const response = await this.apiClient.delete<PermissionOperationResponseDTO>(
            `/rbac/roles/${roleId}/permissions/batch`,
            requestData
        );

        return {
            message: response.message,
            removedPermissions: response.removed_permissions || [],
        };
    }

    async assignPermission(
        roleId: string,
        permissionName: string
    ): Promise<{ message: string }> {
        const requestData: AssignPermissionDTO = {
            permission_name: permissionName,
        };

        const response = await this.apiClient.post<AssignPermissionResponseDTO>(
            `/rbac/roles/${roleId}/permissions`,
            requestData
        );

        return {
            message: response.message,
        };
    }

    async assignRoleToUser(userId: string, roleId: string): Promise<void> {
        const requestData: AssignRoleToUserDTO = {
            role_id: roleId,
        };

        await this.apiClient.post(`/rbac/users/${userId}/roles`, requestData);
    }

    async assignRolesToUser(userId: string, roleIds: string[]): Promise<void> {
        const requestData: AssignRolesToUserDTO = {
            role_ids: roleIds,
        };

        await this.apiClient.post(`/rbac/users/${userId}/roles`, requestData);
    }

    async removeRolesFromUser(userId: string, roleIds: string[]): Promise<void> {
        const requestData: RemoveRolesFromUserDTO = {
            role_ids: roleIds,
        };

        await this.apiClient.delete(`/rbac/users/${userId}/roles`, requestData);
    }
}
